import type { ReactNode } from 'react';
import { formatInitials } from '@brimo/shared';
import type { PublicAdvisor } from './api';
import { channelShort } from './format';
import styles from './Booking.module.css';

export interface Offer {
  organizationName: string;
  advisor: PublicAdvisor | null;
  eventTypeName: string;
  description: string | null;
  durationMinutes: number;
  durationLabel: string;
  channelLine: string;
}

function organizationInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return formatInitials(parts[0] ?? '', parts.length > 1 ? (parts[parts.length - 1] ?? '') : '');
}

/** Host of the offer: the single advisor, or the organization for a round-robin type (`advisor: null`). */
function host(offer: Offer): { name: string; initials: string; roleLine: string | null } {
  return offer.advisor
    ? { name: offer.advisor.name, initials: offer.advisor.initials, roleLine: offer.advisor.roleLine }
    : {
        name: offer.organizationName,
        initials: organizationInitials(offer.organizationName),
        roleLine: null,
      };
}

export function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.fact}>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>{value}</span>
    </div>
  );
}

/** The left column of Buchungsseite 01–04/06 (360 px): advisor, event type, description, facts. */
export function OfferPanel({
  offer,
  before,
  appointmentLabel,
}: {
  offer: Offer;
  /** Above the advisor („Termin umbuchen" + „Bisher", Buchungsseite 06). */
  before?: ReactNode | undefined;
  /** „Ihr Termin" row (Buchungsseite 02/03). */
  appointmentLabel?: string | null | undefined;
}) {
  const person = host(offer);
  return (
    <div className={styles.offer}>
      {before}
      <div className={styles.advisor}>
        <span className={styles.avatar} aria-hidden="true">
          {person.initials}
        </span>
        <div>
          <div className={styles.advisorName}>{person.name}</div>
          {person.roleLine ? <div className={styles.advisorRole}>{person.roleLine}</div> : null}
        </div>
      </div>
      <div>
        <h1 className={styles.offerTitle}>{offer.eventTypeName}</h1>
        {offer.description ? <p className={styles.offerText}>{offer.description}</p> : null}
      </div>
      <div className={styles.facts}>
        <Fact label="Dauer" value={offer.durationLabel} />
        <Fact label="Ort" value={offer.channelLine} />
        {appointmentLabel ? <Fact label="Ihr Termin" value={appointmentLabel} /> : null}
      </div>
    </div>
  );
}

/** The compact offer of the phone frames (Buchungsseite 09/10): avatar 40, type, „Jonas Weiler · 30 Min · Video". */
export function OfferLine({ offer }: { offer: Offer }) {
  const person = host(offer);
  return (
    <div className={styles.offerLine}>
      <span className={styles.avatarSmall} aria-hidden="true">
        {person.initials}
      </span>
      <div className={styles.offerLineText}>
        <h1 className={styles.offerLineTitle}>{offer.eventTypeName}</h1>
        <div className={styles.offerLineMeta}>
          {person.name} · {offer.durationMinutes} Min · {channelShort(offer.channelLine)}
        </div>
      </div>
    </div>
  );
}
